import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NumericFormat } from 'react-number-format'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box, Divider, TextField, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'
import { useDebouncedCallback } from 'use-debounce'

import { useGetMiscItemsQuery, useUpdateMiscItemMutation } from '@/app/services/miscItems'
import { useCalculateQuoteMutation, useGetQuoteQuery } from '@/app/services/quotes'
import { selectLocale } from '@/app/slices/appSlice'
import { selectCurrencyCode, selectCurrencySymbol, selectOrganisationId } from '@/app/slices/organisationSlice'
import { selectedQuoteStatus } from '@/app/slices/quoteItemsSlice'
import { useCurrencyFormatter } from '@/common/hooks'
import { getCurrencyFormat, getFormattedPrice, QUOTE_STATUS_ARRAY } from '@/common/utils'

const classes = {
    pricesSection: {
        width: '300px',
        gap: '12px',
    },
    pricePercentage: {
        fontSize: '14px',
        color: 'text.secondary',
    },
    bold: {
        fontWeight: 'bold',
    },
    itemColumn: {
        display: 'flex',
        flex: 1,
    },
    alignRight: {
        justifyContent: 'flex-end',
    },
    inputColumn: {
        pl: 2,
    },
}

const ItemRow = ({ children }) => (
    <Box
        sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
        }}
    >
        {children}
    </Box>
)

const MiscItemPrices = ({ miscItemId }) => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { quoteId } = useParams()

    const organisationId = useSelector(selectOrganisationId)
    const currencySymbol = useSelector(selectCurrencySymbol)
    const locale = useSelector(selectLocale)
    const currencyCode = useSelector(selectCurrencyCode)

    const { formatCurrency } = useCurrencyFormatter({
        currencyCode,
        locale,
    })

    const { isFetching: isFetchingQuote } = useGetQuoteQuery({ organisationId, quoteId })
    const quoteNotCalculated = useSelector(selectedQuoteStatus) !== QUOTE_STATUS_ARRAY.Calculated
    const { miscItem } = useGetMiscItemsQuery(
        {
            organisationId,
            quoteId,
        },
        {
            selectFromResult: ({ data }) => ({
                miscItem: data?.find((item) => item.id === miscItemId),
            }),
        }
    )

    const [calculateQuoteMutation, { isLoading: isCalculating }] = useCalculateQuoteMutation({
        fixedCacheKey: 'shared-calculate-quote',
    })

    const [updateMiscItem] = useUpdateMiscItemMutation()

    const { isPriceAdjustmentPercentage, itemPrice, linePrice, price, priceAdjustment, priceAdjustmentPercentage } =
        miscItem ?? {}

    const currencyFormat = getCurrencyFormat(currencyCode, locale)
    const placeholder = getFormattedPrice(0, currencyCode, locale, true)

    // In % mode the backend sets the amount from the %, so do the same: a unit price edit caches the new price
    // before the new amount comes back. Otherwise the amount is what was saved, and the % follows it.
    const savedPercentage = isPriceAdjustmentPercentage
        ? (priceAdjustmentPercentage ?? 0)
        : price
          ? ((priceAdjustment ?? 0) / price) * 100
          : 0
    const savedAmount = isPriceAdjustmentPercentage ? Math.round(price * savedPercentage) / 100 : priceAdjustment

    // The inputs hold what the user types, and follow the saved item whenever its price or adjustment changes
    const savedValues = `${price}|${priceAdjustment}|${priceAdjustmentPercentage}|${isPriceAdjustmentPercentage}`
    const [adjustment, setAdjustment] = useState({ savedValues, percentage: savedPercentage, amount: savedAmount })
    if (adjustment.savedValues !== savedValues) {
        setAdjustment({ savedValues, percentage: savedPercentage, amount: savedAmount })
    }

    const calculateQuote = async () => {
        try {
            await calculateQuoteMutation({ organisationId, quoteId }).unwrap()
        } catch (error) {
            const errorMessage = t(error.data)
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 5000 })
        }
    }

    const handleUpdateQuoteItem = async (miscItem) => {
        try {
            await updateMiscItem({
                organisationId,
                quoteId,
                miscItemId: miscItem?.id,
                miscItem,
            }).unwrap()
            calculateQuote()
        } catch (error) {
            enqueueSnackbar(t('Failed to update the $t(quote) item'), {
                variant: 'error',
            })
        }
    }

    const debounceHandleUpdateQuoteItem = useDebouncedCallback(handleUpdateQuoteItem, 1000)

    if (!miscItem) return null

    // Both values are sent, matching the backend's rounding, because the save updates the cached item straight away
    const handlePercentageAdjustmentChange = async (values, sourceInfo) => {
        if (sourceInfo.source === 'event') {
            debounceHandleUpdateQuoteItem.cancel()
            const percentage = values.floatValue ?? 0
            const amount = Math.round(price * percentage) / 100
            setAdjustment({ savedValues, percentage, amount })
            debounceHandleUpdateQuoteItem({
                ...miscItem,
                priceAdjustment: amount,
                priceAdjustmentPercentage: percentage,
                isPriceAdjustmentPercentage: true,
            })
        }
    }

    const handleAmountAdjustmentChange = async (values, sourceInfo) => {
        if (sourceInfo.source === 'event') {
            debounceHandleUpdateQuoteItem.cancel()
            const amount = values.floatValue ?? 0
            const percentage = price ? (amount / price) * 100 : 0
            setAdjustment({ savedValues, percentage, amount })
            debounceHandleUpdateQuoteItem({
                ...miscItem,
                priceAdjustment: amount,
                priceAdjustmentPercentage: percentage,
                isPriceAdjustmentPercentage: false,
            })
        }
    }

    return (
        <Box
            sx={[
                {
                    alignItems: 'flex-start',
                    display: 'flex',
                    flex: '0 0 auto',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                },
                classes.pricesSection,
            ]}
        >
            <ItemRow>
                <Typography
                    data-testid="quote-item-prices-section-title"
                    sx={classes.bold}
                >
                    {t('Prices')}
                </Typography>
            </ItemRow>
            <ItemRow>
                <Typography
                    data-testid="nqp-quote-item-subtotal-label"
                    sx={classes.bold}
                >
                    {t('Subtotal')}
                </Typography>
                <Typography sx={classes.bold}>
                    {quoteNotCalculated || isCalculating || isFetchingQuote || !price
                        ? String.fromCharCode(8212)
                        : formatCurrency(price)}
                </Typography>
            </ItemRow>
            <ItemRow>
                <Typography data-testid="nqp-quote-item-adjustment-label">{t('Adjust')}</Typography>
                <Typography sx={[classes.itemColumn, classes.alignRight, classes.inputColumn]}>
                    <NumericFormat
                        allowNegative={true}
                        customInput={TextField}
                        decimalScale={2}
                        decimalSeparator={currencyFormat.decimal}
                        inputProps={{
                            style: { textAlign: 'right' },
                            'data-testid': 'nqp-quote-misc-item-adjustment-text',
                        }}
                        placeholder="0 %"
                        size="small"
                        suffix=" %"
                        sx={{ '.MuiInputRoot.MuiDisabled': classes.disabled }}
                        thousandSeparator={currencyFormat.group}
                        value={adjustment.percentage}
                        variant="outlined"
                        onValueChange={handlePercentageAdjustmentChange}
                    />
                </Typography>
                <Typography
                    data-testid="nqp-quote-item-adjustment-value"
                    sx={[classes.itemColumn, classes.alignRight, classes.inputColumn]}
                >
                    <NumericFormat
                        allowNegative={true}
                        customInput={TextField}
                        decimalScale={currencyFormat.decimalPlaces}
                        decimalSeparator={currencyFormat.decimal}
                        fixedDecimalScale={true}
                        inputProps={{
                            style: { textAlign: 'right' },
                        }}
                        placeholder={placeholder}
                        prefix={currencyFormat.currencySymbolPosition === 'before' ? currencySymbol : undefined}
                        size="small"
                        suffix={currencyFormat.currencySymbolPosition === 'after' ? currencySymbol : undefined}
                        sx={{ '.MuiInputRoot.MuiDisabled': classes.disabled }}
                        thousandSeparator={currencyFormat.group}
                        value={adjustment.amount ?? null}
                        variant="outlined"
                        onValueChange={handleAmountAdjustmentChange}
                    />
                </Typography>
            </ItemRow>
            <Divider
                orientation="horizontal"
                flexItem
            />
            <ItemRow>
                <Typography
                    data-testid="nqp-quote-item-unit-price-label"
                    sx={[classes.itemColumn, classes.bold]}
                >
                    {t('Unit price')}
                </Typography>
                <Typography sx={[classes.itemColumn, classes.pricePercentage, classes.alignRight, classes.bold]}>
                    100%
                </Typography>
                <Typography sx={[classes.itemColumn, classes.alignRight, classes.bold]}>
                    {quoteNotCalculated || isCalculating || isFetchingQuote || !itemPrice
                        ? String.fromCharCode(8212)
                        : formatCurrency(itemPrice)}
                </Typography>
            </ItemRow>
            <ItemRow>
                <Typography
                    data-testid="nqp-quote-item-line-price-label"
                    sx={classes.bold}
                >
                    {t('Line price')}
                </Typography>
                <Typography sx={classes.bold}>
                    {quoteNotCalculated || isCalculating || isFetchingQuote || !linePrice
                        ? String.fromCharCode(8212)
                        : formatCurrency(linePrice)}
                </Typography>
            </ItemRow>
        </Box>
    )
}
MiscItemPrices.propTypes = {
    miscItemId: PropTypes.string,
}
export default memo(MiscItemPrices)
